/**
 * Voice is not optional. A first grader cannot reliably type a question or read
 * a paragraph, so hold-to-talk and read-aloud are the primary controls, not
 * accessibility extras bolted on afterwards.
 *
 * Both use the browser's built-in APIs — no cloud speech service, so no child's
 * voice ever leaves the device. On the Capacitor iPad build these map onto
 * WKWebView's native speech support.
 */

/* ------------------------------------------------------------------ */
/* read aloud                                                           */
/* ------------------------------------------------------------------ */

export function canSpeak(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

let currentUtterance: SpeechSynthesisUtterance | null = null;

export function speak(text: string, onEnd?: () => void): void {
  if (!canSpeak()) return;
  stopSpeaking();

  const utterance = new SpeechSynthesisUtterance(text);
  // Slower than default, because the point is comprehension not speed.
  utterance.rate = 0.92;
  utterance.pitch = 1.05;
  utterance.lang = "en-US";
  utterance.onend = () => {
    currentUtterance = null;
    onEnd?.();
  };
  utterance.onerror = () => {
    currentUtterance = null;
    onEnd?.();
  };

  currentUtterance = utterance;
  window.speechSynthesis.speak(utterance);
}

export function stopSpeaking(): void {
  if (!canSpeak()) return;
  window.speechSynthesis.cancel();
  currentUtterance = null;
}

export function isSpeaking(): boolean {
  return currentUtterance !== null;
}

/* ------------------------------------------------------------------ */
/* hold to talk                                                         */
/* ------------------------------------------------------------------ */

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
}

type RecognitionConstructor = new () => SpeechRecognitionLike;

function recognitionConstructor(): RecognitionConstructor | undefined {
  if (typeof window === "undefined") return undefined;
  const w = window as unknown as {
    SpeechRecognition?: RecognitionConstructor;
    webkitSpeechRecognition?: RecognitionConstructor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
}

export function canListen(): boolean {
  return recognitionConstructor() !== undefined;
}

export interface Listener {
  stop(): void;
}

/**
 * Starts listening and streams partial transcripts back, so the input field
 * fills in as the child speaks — seeing their words appear is what tells them
 * it's working. Returns a handle the caller stops on release.
 */
export function listen(handlers: {
  onPartial: (text: string) => void;
  onFinal: (text: string) => void;
  onError: (message: string) => void;
}): Listener | null {
  const Recognition = recognitionConstructor();
  if (!Recognition) {
    handlers.onError("This device can't listen. You can type instead.");
    return null;
  }

  const recognition = new Recognition();
  recognition.lang = "en-US";
  recognition.continuous = false;
  recognition.interimResults = true;

  let latest = "";

  recognition.onresult = (event) => {
    let text = "";
    for (let i = 0; i < event.results.length; i += 1) {
      const alternative = event.results[i]?.[0];
      if (alternative) text += alternative.transcript;
    }
    latest = text.trim();
    handlers.onPartial(latest);
  };

  recognition.onerror = (event) => {
    if (event.error === "not-allowed" || event.error === "service-not-allowed") {
      handlers.onError("I need permission to hear you. Ask a grown-up.");
    } else if (event.error !== "aborted" && event.error !== "no-speech") {
      handlers.onError("I didn't catch that. Try again.");
    }
  };

  recognition.onend = () => {
    if (latest) handlers.onFinal(latest);
  };

  try {
    recognition.start();
  } catch {
    handlers.onError("I couldn't start listening. Try again.");
    return null;
  }

  return { stop: () => recognition.stop() };
}
